import React from 'react';
import { cx, FOCUS_RING } from './styles';

/**
 * Two-to-four option switch: Events/Tickets, Venues/Promoters,
 * All/Select Accounts, Monthly/Annual, Transfers' source pills.
 *
 * A `role="group"` of real buttons with `aria-pressed` — never a
 * div-with-onClick. Each call site keeps its own state and its own labels; this
 * component owns no state.
 */
export interface SegmentOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Optional per-option active colour, e.g. Transfers' per-source tints. */
  activeClassName?: string;
  title?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name for the group. */
  label: string;
  size?: 'sm' | 'md';
  /** Stretch each option to fill the container (mobile action rows). */
  stretch?: boolean;
  /** Onboarding-tour anchor, rendered as `data-tour`. */
  tourId?: string;
  className?: string;
}

const sizeStyles = {
  sm: 'text-xs px-3 py-1.5',
  md: 'text-sm px-4 py-2',
};

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
  stretch,
  tourId,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      data-tour={tourId}
      aria-label={label}
      className={cx(
        'inline-flex rounded-lg border border-white/10 bg-white/[0.03] p-1',
        stretch && 'w-full',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            title={option.title}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={cx(
              'rounded-md font-medium transition-all disabled:cursor-not-allowed disabled:opacity-40',
              sizeStyles[size],
              stretch && 'flex-1',
              active
                ? option.activeClassName || 'bg-[#3b82f6]/15 text-[#60a5fa]'
                : 'text-white/50 hover:text-white/80',
              FOCUS_RING,
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
